import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { noticeAPI } from '../services/api';

interface NoticeFormData {
  min_sell_price: string;
  min_price_allowed: string;
  share_quantity_total: string;
  start_date: string;
  end_date: string;
  notes: string;
}

interface FormErrors {
  min_sell_price?: string;
  share_quantity_total?: string;
  start_date?: string;
  end_date?: string;
}

interface Notice {
  id: string;
  notice_number: string;
  status: string;
  min_sell_price: number;
  min_price_allowed?: number;
  share_quantity_total: number;
  start_date: string;
  end_date: string;
  notes?: string;
}

const EditNotice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [formData, setFormData] = useState<NoticeFormData>({
    min_sell_price: '',
    min_price_allowed: '',
    share_quantity_total: '',
    start_date: '',
    end_date: '',
    notes: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    loadNotice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadNotice = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await noticeAPI.getById(id);
      setNotice(data);
      
      // Populate form with current values
      setFormData({
        min_sell_price: data.min_sell_price?.toString() || '',
        min_price_allowed: data.min_price_allowed?.toString() || '',
        share_quantity_total: data.share_quantity_total?.toString() || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        notes: data.notes || ''
      });
    } catch (err: any) {
      setSubmitError('Failed to load notice');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const price = parseFloat(formData.min_sell_price);
    if (!formData.min_sell_price || isNaN(price) || price <= 0) {
      newErrors.min_sell_price = 'Minimum sell price must be greater than 0';
    }

    const quantity = parseInt(formData.share_quantity_total);
    if (!formData.share_quantity_total || isNaN(quantity) || quantity <= 0) {
      newErrors.share_quantity_total = 'Share quantity must be greater than 0';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    } else if (formData.start_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof NoticeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmitClick = () => {
    if (validateForm()) {
      setConfirmOpen(true);
    }
  };

  const handleConfirmSave = async () => {
    if (!id) return;
    setConfirmOpen(false);
    setSaving(true);
    setSubmitError('');

    try {
      const noticeData = {
        min_sell_price: parseFloat(formData.min_sell_price),
        min_price_allowed: formData.min_price_allowed ? parseFloat(formData.min_price_allowed) : null,
        share_quantity_total: parseInt(formData.share_quantity_total),
        start_date: formData.start_date,
        end_date: formData.end_date,
        notes: formData.notes || null
      };

      await noticeAPI.update(id, noticeData);
      navigate(`/notices/${id}`, { state: { success: 'Notice updated successfully!' } });
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to update notice');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(num);
  };

  const formatNumber = (value: string) => {
    const num = parseInt(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!notice) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Notice not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (notice.status !== 'pending') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Only pending notices can be edited. This notice is <strong>{notice.status}</strong>.
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/notices/${id}`)} sx={{ mt: 2 }}>
          Back to Notice
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/notices/${id}`)} sx={{ mb: 2 }}>
          Back to Notice
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Edit Notice
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {notice.notice_number}
              </Typography>
              <Chip label="PENDING" color="warning" size="small" />
            </Box>
          </Box>
        </Box>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError('')}>
          {submitError}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Pricing Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Pricing Parameters
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Minimum Sell Price"
                type="number"
                value={formData.min_sell_price}
                onChange={handleChange('min_sell_price')}
                error={!!errors.min_sell_price}
                helperText={errors.min_sell_price}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: '0.0001', min: '0' }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Minimum Price Allowed (Optional)"
                type="number"
                value={formData.min_price_allowed}
                onChange={handleChange('min_price_allowed')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: '0.0001', min: '0' }}
              />
            </Grid>

            {/* Quantity Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Share Quantity
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Total Shares Authorized"
                type="number"
                value={formData.share_quantity_total}
                onChange={handleChange('share_quantity_total')}
                error={!!errors.share_quantity_total}
                helperText={errors.share_quantity_total}
                inputProps={{ min: '1' }}
                required
              />
            </Grid>

            {/* Date Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Active Period
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                error={!!errors.start_date}
                helperText={errors.start_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                error={!!errors.end_date}
                helperText={errors.end_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.start_date }}
                required
              />
            </Grid>

            {/* Notes Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange('notes')}
              />
            </Grid>
          </Grid>

          {/* Summary Preview */}
          <Paper variant="outlined" sx={{ mt: 4, p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Updated Notice Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">Min Sell Price</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatCurrency(formData.min_sell_price)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">Shares Authorized</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatNumber(formData.share_quantity_total)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">Start Date</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formData.start_date || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formData.end_date || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Actions */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(`/notices/${id}`)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              disabled={saving}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to update notice <strong>{notice.notice_number}</strong> with the following parameters:
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Min Sell Price:</strong> {formatCurrency(formData.min_sell_price)}</Typography>
            <Typography variant="body2"><strong>Shares Authorized:</strong> {formatNumber(formData.share_quantity_total)}</Typography>
            <Typography variant="body2"><strong>Period:</strong> {formData.start_date} to {formData.end_date}</Typography>
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            This change will be recorded in the audit trail.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} variant="contained" autoFocus>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditNotice;

